import * as React from "react";

import {
  Box,
  makeStyles,
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  Typography,
} from "@material-ui/core";
import * as Redux from "react-redux";
import { useLocation } from "react-router-dom";

import { AccordionHeader, AccordionHeaderIcon } from "client/components";
import { FormCompletion } from "client/domain";
import { useTranslation } from "client/hooks";
import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";
import { RootState } from "client/store";
import { FormUrlParam } from "client/utils";

const useStyles = makeStyles((theme) => {
  const {
    accordianHeaderBorder,
    accordianHeaderFlex,
  } = useNormalizedBrandPaletteValues(theme);

  return {
    accordionDetailsContainer: {
      paddingBottom: theme.spacing(1),
    },
    header: {
      fontWeight: theme.typography.fontWeightBold,
      marginRight: theme.spacing(1),
      flex: accordianHeaderFlex,
    },
    headerContainer: {
      borderBottom: accordianHeaderBorder,
    },
  };
});

function getPreviousPanels(panel: FormPanel | false): FormPanel[] {
  switch (panel) {
    case "Store Details":
      return ["Company Information"];
    case "Beneficial Owners":
      return ["Store Details", "Company Information"];
    default:
      return [];
  }
}

function isPrevPanelOpenOrNone(
  currentOpenPanel: FormPanel | false,
  clickedPanel: FormPanel
): boolean {
  if (!currentOpenPanel) return true;
  const previousPanels = getPreviousPanels(clickedPanel);
  return previousPanels.includes(currentOpenPanel);
}

function isCurrentFormComplete(
  form: FormPanel,
  formCompletionState: FormCompletion
): boolean {
  switch (form) {
    case "Store Details":
      return formCompletionState.storeDetailsForm;
    case "Beneficial Owners":
      return formCompletionState.businessOwnerForm;
    case "Company Information":
      return formCompletionState.companyInfoForm;
    default:
      return false;
  }
}

function isPreviousFormComplete(
  form: FormPanel,
  formCompletionState: FormCompletion
): boolean {
  switch (form) {
    case "Store Details":
      return formCompletionState.companyInfoForm;
    case "Beneficial Owners":
      return formCompletionState.storeDetailsForm;
    case "Company Information":
      return true;
    default:
      return false;
  }
}

function isPanelExpandable(
  form: FormPanel,
  formCompletionState: FormCompletion,
  currentExpandedPanel: FormPanel | false
): boolean {
  if (isCurrentFormComplete(form, formCompletionState)) {
    return true;
  }
  if (
    isPreviousFormComplete(form, formCompletionState) &&
    isPrevPanelOpenOrNone(currentExpandedPanel, form)
  ) {
    return true;
  }
  return false;
}

const urlToFormGroupMap: { [key in FormUrlParam]?: FormPanel } = {
  "beneficial-owners": "Beneficial Owners",
  "company-information": "Company Information",
  "store-details": "Store Details",
};

/**
 * Determining which panel is expanded starts with
 * 1st checking the url for a parameter
 * 2nd based on form completion values
 *
 * @param urlParam oneof params in the URL
 * @param formCompletionState
 * @returns either a panel to open or false in which no panels are open
 */
function getWhichPanelExpanded(
  urlParam: FormUrlParam,
  formCompletionState: FormCompletion
): FormPanel | false {
  if (urlParam) {
    const panelFromUrl = urlToFormGroupMap[urlParam];
    // if we open a panel from url params, make sure previous form is complete before opening that form so they're not skipping forms
    if (
      panelFromUrl &&
      isPreviousFormComplete(panelFromUrl, formCompletionState)
    ) {
      return panelFromUrl;
    }
  }

  // form completion -> view logic below
  const {
    businessOwnerForm,
    companyInfoForm,
    storeDetailsForm,
  } = formCompletionState;
  if (!companyInfoForm) return "Company Information";
  if (!storeDetailsForm) return "Store Details";
  if (!businessOwnerForm) return "Beneficial Owners";
  // if all complete none are open
  return false;
}

type FormPanel = "Store Details" | "Company Information" | "Beneficial Owners";

export interface Props {
  businessOwnerForm: React.ReactElement;
  companyInformationForm: React.ReactElement;
  storeDetailsForm: React.ReactElement;
}

const info = {
  companyInfo: {
    "aria-controls": "companyInformation-content",
    "data-testid": "companyInfo-header",
    id: "companyInformation-header",
    labelkey: "uiOnboardingAccordianCompanyInfoLabel",
  },
  storeDetails: {
    "aria-controls": "storeDetails-content",
    "data-testid": "storeDetails-header",
    id: "storeDetails-header",
    labelkey: "uiOnboardingAccordianStoreDetailsLabel",
  },
  businessOwners: {
    "aria-controls": "businessOwners-content",
    "data-testid": "businessOwners-header",
    id: "businessOwners-header",
    labelkey: "uiOnboardingAccordianBusinessOwnersLabel",
  },
};

export const OnboardingAccordion: React.FC<Props> = ({
  companyInformationForm,
  businessOwnerForm,
  storeDetailsForm,
}) => {
  const {
    error: {
      fieldErrorMap: { companyInformation, locations, storeDetails, owners },
    },
    formCompletion,
  } = Redux.useSelector(({ error, formCompletion }: RootState) => ({
    error,
    formCompletion,
  }));
  const location = useLocation();
  const [expanded, setExpanded] = React.useState<FormPanel | false>(false);
  const t = useTranslation();
  const classes = useStyles();

  const companyInfoErrors = Object.keys(companyInformation ?? {});
  const storeDetailsErrors = Object.keys(storeDetails ?? {});
  const storeLocationErrors = Object.keys(locations ?? {});
  const ownerErrors = (owners ?? []).filter((m) => Object.keys(m).length);

  const handleChange = (panel: FormPanel) => (
    event: React.ChangeEvent<Record<string, unknown>>,
    newExpanded: boolean
  ) => {
    if (isPanelExpandable(panel, formCompletion, expanded)) {
      setExpanded(newExpanded ? panel : false);
    }
  };

  React.useEffect(() => {
    const segments = location.pathname.split("/");
    const formUrl = segments.pop() as FormUrlParam;
    const next = getWhichPanelExpanded(formUrl, formCompletion);
    setExpanded(next);
  }, [formCompletion, location.pathname]);

  return (
    <Box>
      <MuiAccordion
        expanded={expanded === "Company Information"}
        onChange={handleChange("Company Information")}
        square
      >
        <AccordionHeader
          {...info.companyInfo}
          expanded={expanded === "Company Information"}
          isExpandable={isPanelExpandable(
            "Company Information",
            formCompletion,
            expanded
          )}
          className={classes.headerContainer}
        >
          <Typography className={classes.header} variant="body2">
            {t(info.companyInfo.labelkey)}
          </Typography>
          <AccordionHeaderIcon
            isError={companyInfoErrors.length > 0}
            show={formCompletion.companyInfoForm}
          />
        </AccordionHeader>
        <MuiAccordionDetails
          role="region"
          className={classes.accordionDetailsContainer}
        >
          {companyInformationForm}
        </MuiAccordionDetails>
      </MuiAccordion>
      <MuiAccordion
        expanded={expanded === "Store Details"}
        onChange={handleChange("Store Details")}
        square
      >
        <AccordionHeader
          {...info.storeDetails}
          expanded={expanded === "Store Details"}
          isExpandable={isPanelExpandable(
            "Store Details",
            formCompletion,
            expanded
          )}
          className={classes.headerContainer}
        >
          <Typography className={classes.header} variant="body2">
            {t(info.storeDetails.labelkey)}
          </Typography>
          <AccordionHeaderIcon
            isError={
              storeDetailsErrors.length > 0 || storeLocationErrors.length > 0
            }
            show={formCompletion.storeDetailsForm}
          />
        </AccordionHeader>
        <MuiAccordionDetails
          role="region"
          className={classes.accordionDetailsContainer}
        >
          {storeDetailsForm}
        </MuiAccordionDetails>
      </MuiAccordion>
      <MuiAccordion
        expanded={expanded === "Beneficial Owners"}
        onChange={handleChange("Beneficial Owners")}
        square
      >
        <AccordionHeader
          {...info.businessOwners}
          expanded={expanded === "Beneficial Owners"}
          isExpandable={isPanelExpandable(
            "Beneficial Owners",
            formCompletion,
            expanded
          )}
        >
          <Typography className={classes.header} variant="body2">
            {t(info.businessOwners.labelkey)}
          </Typography>
          <AccordionHeaderIcon
            isError={ownerErrors.length > 0}
            show={formCompletion.businessOwnerForm}
          />
        </AccordionHeader>
        <MuiAccordionDetails
          role="region"
          className={classes.accordionDetailsContainer}
        >
          {businessOwnerForm}
        </MuiAccordionDetails>
      </MuiAccordion>
    </Box>
  );
};
